/**
 * 3-way segmented control: OFF | Rules (hot) | Reference (cold)
 * iOS-style; optional loading spinner inside the active segment.
 */
import React from 'react';

/** @typedef {'off' | 'hot' | 'cold'} SegmentValue */

const SEGMENTS = [
  { value: 'off', label: 'Off' },
  { value: 'hot', label: 'Rules' },
  { value: 'cold', label: 'Reference' }
];

export default function MemorySegmentedControl({
  value,
  loading = false,
  disabled = false,
  onChange,
  idPrefix = 'mem-seg'
}) {
  return (
    <div
      className="inline-flex rounded-lg bg-gray-200/90 p-0.5 gap-0.5 shadow-inner border border-gray-200/80"
      role="radiogroup"
      aria-busy={loading}
      aria-disabled={disabled || loading}
    >
      {SEGMENTS.map((seg) => {
        const selected = value === seg.value;
        const showSpinner = loading && selected;
        return (
          <button
            key={seg.value}
            type="button"
            role="radio"
            aria-checked={selected}
            id={`${idPrefix}-${seg.value}`}
            disabled={disabled || loading}
            onClick={() => !loading && !disabled && onChange?.(seg.value)}
            className={[
              'relative flex-1 min-w-[4.25rem] px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wide transition-all',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
              selected
                ? loading
                  ? 'bg-white/95 text-gray-900 shadow-sm'
                  : 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-800',
              disabled || loading ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'
            ].join(' ')}
          >
            <span className={`flex items-center justify-center gap-1 ${showSpinner ? 'opacity-90' : ''}`}>
              {showSpinner && (
                <svg
                  className="h-3 w-3 animate-spin text-blue-600 flex-shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              )}
              {seg.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
